import { createAsyncThunk } from '@reduxjs/toolkit';

import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import type { DashboardTab, MarketApps, TileBigNumber } from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import type { RootState } from '@/store';

import {
  initializeDraft,
  initializeOtherDashboardDraft,
  createDashboardDraft,
  moveTabToOtherDashboard as moveTabAction,
  moveTileToOtherDashboard as moveTileAction,
  resetDraft,
  setAllDashboardsData
} from './dashboardEdit.slice';
import {
  getTabValidationErrorMessage,
  getTileValidationErrorMessage,
  isCurrentDashboard,
  validateTabMove,
  validateTileAddition
} from './utils/helpers';
import { mapServerDataToDraft } from './utils/mapping';

interface InitializePayload {
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  currentDashboardTabs: DashboardTab;
  tabNames: string[];
  activeTab: number;
}

export const initializeDashboardEditThunk = createAsyncThunk(
  'dashboardEdit/initialize',
  (payload: InitializePayload, { dispatch }) => {
    const { dashboardName, initialDashboardName, currentDashboardTabs, activeTab } = payload;

    const { draftTabs, tabDrafts } = mapServerDataToDraft(
      currentDashboardTabs,
      dashboardName,
      activeTab
    );

    dispatch(
      initializeDraft({
        dashboardName,
        initialDashboardName,
        draftTabs,
        tabDrafts
      })
    );
  }
);

/**
 * Create a new dashboard draft with a unique name based on current state.
 */
export const createUniqueDashboardDraftThunk = createAsyncThunk<
  { dashboardName: string },
  void,
  { state: RootState }
>('dashboardEdit/createUniqueDashboardDraft', (_, { getState, dispatch }) => {
  const state = getState().dashboardEdit;
  const existingNames = new Set(Object.keys(state.allDashboardsData));

  Object.keys(state.otherDashboardDrafts).forEach(name => existingNames.add(name));

  if (state.dashboardName) {
    existingNames.add(state.dashboardName);
  }

  if (state.initialDashboardName) {
    existingNames.add(state.initialDashboardName);
  }

  const dashboardName = getUniqueDashboardName(Array.from(existingNames));
  dispatch(createDashboardDraft({ name: dashboardName }));
  return { dashboardName };
});

interface MoveTilePayload {
  targetDashboardName: string;
  targetTabId: string;
  tile: TileBigNumber;
}

/**
 * Thunk for moving a tile to another dashboard with validation.
 * Validates:
 * - Maximum 8 tiles per tab
 * - No duplicate tile codes in the same tab
 *
 * If target dashboard doesn't have a draft, automatically initializes it
 * from allDashboardsData to preserve existing tabs.
 *
 * Returns error message on validation failure, undefined on success.
 */
export const moveTileToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveTilePayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/moveTileToOtherDashboard', (payload, { getState, dispatch, rejectWithValue }) => {
  const { targetDashboardName, targetTabId, tile } = payload;
  let state = getState().dashboardEdit;

  // Initialize target dashboard draft from server data if needed
  if (
    !isCurrentDashboard(state, targetDashboardName) &&
    !state.otherDashboardDrafts[targetDashboardName]
  ) {
    const serverData = state.allDashboardsData[targetDashboardName];
    if (serverData) {
      dispatch(
        initializeOtherDashboardDraft({
          dashboardName: targetDashboardName,
          serverData
        })
      );
      // Refresh state after initialization
      state = getState().dashboardEdit;
    }
  }

  // Get or create the target tab draft for validation
  let targetTabDraft;

  if (isCurrentDashboard(state, targetDashboardName)) {
    targetTabDraft = state.tabDrafts[targetTabId];
    if (!targetTabDraft) {
      return rejectWithValue('Target tab not found');
    }
  } else {
    // For other dashboards, we need to check or simulate the draft
    const dashboardDraft = state.otherDashboardDrafts[targetDashboardName] || {
      draftTabs: [],
      tabDrafts: {}
    };
    targetTabDraft = dashboardDraft.tabDrafts[targetTabId] || { marketTabApps: [], tiles: [] };
  }

  // Validate tile addition
  const validationError = validateTileAddition(targetTabDraft, tile);

  if (validationError) {
    return rejectWithValue(getTileValidationErrorMessage(validationError));
  }

  // Validation passed, dispatch the action
  dispatch(moveTileAction({ targetDashboardName, targetTabId, tile }));
});

interface MoveTabPayload {
  targetDashboardName: string;
  tabId: string;
  tabName: string;
}

/**
 * Thunk for moving a tab to another dashboard with validation.
 * Validates:
 * - No duplicate tab names in the target dashboard (case-insensitive)
 *
 * Returns error message on validation failure, undefined on success.
 */
export const moveTabToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveTabPayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/moveTabToOtherDashboard', (payload, { getState, dispatch, rejectWithValue }) => {
  const { targetDashboardName, tabId, tabName } = payload;
  const state = getState().dashboardEdit;

  // Don't move to the same dashboard (no-op)
  if (isCurrentDashboard(state, targetDashboardName)) {
    return;
  }

  // Get target dashboard tabs for validation
  const targetDashboardDraft = state.otherDashboardDrafts[targetDashboardName];
  const targetTabs: any[] = targetDashboardDraft ? targetDashboardDraft.draftTabs : [];

  // Use default name if current name is empty
  const effectiveTabName = tabName.trim() || TAB_DEFAULT_BASE_NAME;

  // Validate tab name
  const validationError = validateTabMove(targetTabs, effectiveTabName);

  if (validationError) {
    return rejectWithValue(getTabValidationErrorMessage(validationError, effectiveTabName));
  }

  // Validation passed, dispatch the action
  dispatch(moveTabAction({ targetDashboardName, tabId, tabName: effectiveTabName }));
});

interface ReinitializePayload {
  dashboardName: string;
  activeTab?: number;
  allDashboardsData?: Record<string, DashboardTab>;
}

/**
 * Reinitialize draft when switching dashboards.
 * If allDashboardsData is provided, updates the cache first.
 * Otherwise, uses existing cached data to create the draft.
 *
 * This thunk handles the case where:
 * 1. The user switches dashboards while in edit mode
 * 2. The draft doesn't exist for the new dashboard
 * 3. But the dashboard data exists in allDashboardsData
 */
export const reinitializeDraftThunk = createAsyncThunk<
  void,
  ReinitializePayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/reinitialize', (payload, { getState, dispatch, rejectWithValue }) => {
  const { dashboardName, activeTab = 0, allDashboardsData: newDashboardsData } = payload;

  // Update cache if new data provided
  if (newDashboardsData) {
    dispatch(setAllDashboardsData({ data: newDashboardsData }));
  }

  // Get current state (may have been updated by setAllDashboardsData)
  const state = getState().dashboardEdit;
  const serverData = newDashboardsData?.[dashboardName] ?? state.allDashboardsData[dashboardName];

  if (!serverData) {
    return rejectWithValue(`Dashboard "${dashboardName}" not found in cached data`);
  }

  const { draftTabs, tabDrafts } = mapServerDataToDraft(serverData, dashboardName, activeTab);

  // Reset current draft and initialize new one
  dispatch(resetDraft());
  dispatch(
    initializeDraft({
      dashboardName,
      initialDashboardName: dashboardName,
      draftTabs,
      tabDrafts
    })
  );
});
